import { useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { ArrowUp, ArrowDown, Trash2, Plus, Lock } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  addExpenseCategory, updateExpenseCategory, deleteExpenseCategory, reorderExpenseCategories,
  addIncomeCategory, deleteIncomeCategory, reorderIncomeCategories
} from '../../../services/settingsService';
import { useData } from '../../store/data';
import { confirmAsk } from '../../components/confirm';
import { suggestClass, useUnclassified } from './classify';

type Kind = 'expense' | 'income';
const uid = () => getAuth().currentUser!.uid;
const fail = (r: any, fb: string) => toast.error(r?.error ?? fb);

/** Số bản ghi đang dùng danh mục theo TÊN (dữ liệu lưu tên, không lưu id). */
function ClassifyReview() {
  const todo = useUnclassified();
  const [picked, setPicked] = useState<Record<string, 'need' | 'want' | undefined>>({});
  const [busy, setBusy] = useState(false);
  const value = (c: any) => (c.id in picked ? picked[c.id] : suggestClass(c.name));
  if (todo.length === 0) return null;
  const chosen = todo.filter((c: any) => value(c));
  const apply = async () => {
    setBusy(true);
    const results: any[] = await Promise.all(chosen.map((c: any) => updateExpenseCategory(uid(), c.id, { budgetClass: value(c) })));
    setBusy(false);
    const bad = results.filter(r => !r?.success).length;
    bad ? toast.error(`${bad} danh mục chưa lưu được`) : toast.success(`Đã lưu phân loại cho ${results.length} danh mục`);
  };
  return (
    <Card className="space-y-3 border-warn/40 bg-warn-soft p-4">
      <div>
        <p className="font-medium">{todo.length} danh mục chi chưa được phân loại</p>
        <p className="text-sm text-muted-foreground">Chưa phân loại thì được tính là "Mong muốn" — làm quy tắc 50/30/20 và chi tiêu thiết yếu (quỹ khẩn cấp) thiếu chính xác. Gợi ý theo tên chỉ để tham khảo: thầy xem lại rồi bấm Áp dụng; danh mục chưa chọn sẽ để nguyên.</p>
      </div>
      <ul className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2">
        {todo.map((c: any) => {
          const v = value(c);
          return (
            <li key={c.id} className="flex items-center justify-between gap-2">
              <span className="truncate text-sm">{c.name}{!(c.id in picked) && v && <span className="ml-1 text-xs text-muted-foreground">(gợi ý)</span>}</span>
              <div className="flex rounded-md border border-border bg-background p-0.5" role="group" aria-label={`Phân loại ${c.name}`}>
                {(['need', 'want'] as const).map(k => (
                  <button key={k} onClick={() => setPicked(p => ({ ...p, [c.id]: v === k ? undefined : k }))} aria-pressed={v === k}
                    className={cn('rounded px-2.5 py-1 text-xs font-medium', v === k ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted')}>
                    {k === 'need' ? 'Thiết yếu' : 'Mong muốn'}
                  </button>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
      <Button onClick={apply} disabled={busy || chosen.length === 0}>Áp dụng cho {chosen.length} danh mục</Button>
    </Card>
  );
}

const usage = (name: string, d: Record<string, any[]>): number => {
  const hit = (rows: any[] | undefined) => (rows ?? []).filter(r => (r.categoryName || r.category) === name || r.category === name).length;
  const inEnv = (d.budgetEnvelopes ?? []).filter(e => e.limits && name in e.limits).length;
  return hit(d.expenses) + hit(d.incomes) + hit(d.plannedExpenses) + hit(d.plannedIncomes) + hit(d.recurringTemplates) + inEnv;
};

export default function CategoriesTab() {
  const data = useData(s => s.data);
  const [kind, setKind] = useState<Kind>('expense');
  const [name, setName] = useState('');
  const [cls, setCls] = useState<'need' | 'want'>('want');
  const [busy, setBusy] = useState(false);
  const rows = useMemo(() => (kind === 'expense' ? data.expenseCategories : data.incomeCategories).slice().sort((a: any, b: any) => (a.order ?? 99) - (b.order ?? 99)), [data, kind]);

  const move = async (i: number, delta: number) => {
    const ids = rows.map((r: any) => r.id);
    const j = i + delta; if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    const r: any = await (kind === 'expense' ? reorderExpenseCategories : reorderIncomeCategories)(uid(), ids);
    if (!r?.success) fail(r, 'Không đổi được thứ tự');
  };
  const setClass = async (id: string, v: 'need' | 'want') => {
    const r: any = await updateExpenseCategory(uid(), id, { budgetClass: v });
    if (!r?.success) fail(r, 'Không cập nhật được');
  };
  const remove = async (row: any) => {
    if (!await confirmAsk({ title: `Xoá danh mục "${row.name}"?` })) return;
    const r: any = await (kind === 'expense' ? deleteExpenseCategory : deleteIncomeCategory)(uid(), row.id);
    r?.success ? toast.success('Đã xoá danh mục') : fail(r, 'Không xoá được');
  };
  const add = async () => {
    const n = name.trim();
    if (!n) { toast.error('Nhập tên danh mục'); return; }
    if (rows.some((r: any) => r.name.trim().toLowerCase() === n.toLowerCase())) { toast.error('Danh mục này đã có'); return; }
    setBusy(true);
    const r: any = await (kind === 'expense' ? addExpenseCategory : addIncomeCategory)(uid(), { name: n });
    if (r?.success && kind === 'expense') await updateExpenseCategory(uid(), r.id, { budgetClass: cls });
    setBusy(false);
    if (r?.success) { toast.success(`Đã thêm "${n}"`); setName(''); } else fail(r, 'Không thêm được');
  };

  return (
    <div className="space-y-4">
      <Tabs value={kind} onValueChange={v => setKind(v as Kind)}><TabsList><TabsTrigger value="expense">Danh mục chi</TabsTrigger><TabsTrigger value="income">Danh mục thu</TabsTrigger></TabsList></Tabs>
      <p className="max-w-prose text-sm text-muted-foreground">
        {kind === 'expense' ? 'Nhãn Thiết yếu / Mong muốn quyết định quy tắc 50/30/20, quỹ khẩn cấp và gợi ý cắt giảm. ' : ''}
        Tên danh mục <b>không đổi được</b> vì mọi giao dịch lưu theo tên — đổi tên sẽ làm giao dịch cũ mất liên kết. Cần tên khác thì thêm danh mục mới.
      </p>
      {kind === 'expense' && <ClassifyReview />}
      <Card className="divide-y divide-border overflow-hidden p-0">
        {rows.map((r: any, i: number) => {
          const used = usage(r.name, data as any);
          const locked = r.isDefault || used > 0;
          return (
            <div key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
              <div className="min-w-0 flex-1 basis-40">
                <p className="truncate text-sm font-medium">{r.name}</p>
                <p className="num text-xs text-muted-foreground">{used > 0 ? `${used} bản ghi đang dùng` : 'chưa dùng'}{r.isDefault ? ' · mặc định' : ''}</p>
              </div>
              {kind === 'expense' && (
                <div className="flex rounded-md border border-border p-0.5" role="group" aria-label={`Phân loại ${r.name}`}>
                  {(['need', 'want'] as const).map(v => (
                    <button key={v} onClick={() => setClass(r.id, v)} aria-pressed={(r.budgetClass === 'need' ? 'need' : 'want') === v}
                      className={cn('rounded px-2.5 py-1 text-xs font-medium', (r.budgetClass === 'need' ? 'need' : 'want') === v ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted')}>
                      {v === 'need' ? 'Thiết yếu' : 'Mong muốn'}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex">
                <Button variant="ghost" size="icon" disabled={i === 0} onClick={() => move(i, -1)} aria-label={`Đưa ${r.name} lên`}><ArrowUp size={15} /></Button>
                <Button variant="ghost" size="icon" disabled={i === rows.length - 1} onClick={() => move(i, 1)} aria-label={`Đưa ${r.name} xuống`}><ArrowDown size={15} /></Button>
                {locked
                  ? <span className="flex h-9 w-9 items-center justify-center text-muted-foreground" title={r.isDefault ? 'Danh mục mặc định không xoá được' : 'Đang được dùng — không xoá được'}><Lock size={14} aria-label="Không xoá được" /></span>
                  : <Button variant="ghost" size="icon" onClick={() => remove(r)} aria-label={`Xoá ${r.name}`}><Trash2 size={15} /></Button>}
              </div>
            </div>
          );
        })}
      </Card>
      <div className="flex flex-wrap items-center gap-2">
        <Input value={name} onChange={e => setName(e.target.value)} placeholder={`Tên danh mục ${kind === 'expense' ? 'chi' : 'thu'} mới`} className="max-w-64" aria-label="Tên danh mục mới" onKeyDown={e => e.key === 'Enter' && add()} />
        {kind === 'expense' && (
          <Tabs value={cls} onValueChange={v => setCls(v as any)}><TabsList><TabsTrigger value="need">Thiết yếu</TabsTrigger><TabsTrigger value="want">Mong muốn</TabsTrigger></TabsList></Tabs>
        )}
        <Button onClick={add} disabled={busy}><Plus size={14} aria-hidden /> Thêm</Button>
      </div>
    </div>
  );
}
